import * as React from 'react';
import { useField } from 'formik';
import { useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { single, getObjectValues, replaceElementInArray } from '../../../lib/utils';
import { Component } from '../../../models/be/db/FormSpecification';
import { getLocaleDateString } from '../../../core/internationalisation';
import EditButton from '../buttons/EditButton';
import DeleteButton from '../buttons/DeleteButton';
import AdditionalName, { createNewAdditionalName } from '../../../models/be/AdditionalName';
// eslint-disable-next-line import/no-cycle
import DataCapturePageSubForm from './DataCapturePageSubForm';
import './AdditionalNamesEditor.less';
import { validateAdditionalNames } from '../../../core/domain';
import MessageBox from '../MessageBox';
import PrimaryButton from '../buttons/PrimaryButton';
import YesNoDialog from '../Dialogs/YesNoDialog';
import PrimaryName from '../../../models/be/PrimaryName';
import { useAsyncAction } from '../../../commonComponents/Hooks';
import { deleteNameChangeDocuments } from '../../../core/api';

const AdditionalNamesEditor = (props: Props) => {
	const [, , performAsyncAction] = useAsyncAction(false);
	const [selectedIndex, setSelectedIndex] = useState<number>(null);
	const [currentAdditionalName, setCurrentAdditionalName] = useState<AdditionalName>(null);
	const [showDialog, setShowDialog] = React.useState(false);
	const fieldName = single(props.fieldNames);
	const formikProps = { name: fieldName };
	const [field, meta, helpers] = useField(formikProps);
	const additionalNames = field.value as AdditionalName[];

	React.useEffect(() => {
		performAsyncAction(async () => {
			if (additionalNames.length === 0) {
				await deleteNameChangeDocuments(props.workflowId);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [additionalNames.length, props.workflowId]);

	const setNavigationButtonsDisabled = (value: boolean) => {
		if (props.setSubmitButtonsDisabled) props.setSubmitButtonsDisabled(value);
		if (props.setBackButtonsDisabled) props.setBackButtonsDisabled(value);
	};

	const handleEditButtonClick = (index: number) => () => {
		setNavigationButtonsDisabled(true);
		setSelectedIndex(index);
	};

	const handleDeleteButtonClick = (index: number) => () => {
		setSelectedIndex(index);
		setShowDialog(true);
	};

	const handleYes = async () => {
		try {
			await deleteRecord(selectedIndex);
		} finally {
			setShowDialog(false);
			setNavigationButtonsDisabled(false);
		}
	};

	const handleNo = () => {
		setShowDialog(false);
		setSelectedIndex(null);
	};

	const deleteRecord = async (index: number) => {
		// Must create a new array and setValue for it, so that formik.dirty will be udpated.
		const updatedAdditionalNames = additionalNames.filter((a, i) => i !== index);
		helpers.setValue(updatedAdditionalNames);
		setSelectedIndex(null);

		if (updatedAdditionalNames.length === 0) {
			await performAsyncAction(async () => {
				await deleteNameChangeDocuments(props.workflowId);
			});
		}
	};

	const handleAddButtonClick = () => {
		setNavigationButtonsDisabled(true);
		const newAdditionalName = createNewAdditionalName();
		setCurrentAdditionalName(newAdditionalName);
	};

	const resetStatesOfClosingForm = () => {
		setNavigationButtonsDisabled(false);
		setSelectedIndex(null);
		setCurrentAdditionalName(null);
	};

	const handleSaveButtonClick = (values: { additionalName: AdditionalName }) => {
		const updatedAdditionalNames = replaceElementInArray(additionalNames, values.additionalName, selectedIndex);
		helpers.setValue(updatedAdditionalNames);
		resetStatesOfClosingForm();
	};

	return (
		<>
			{showDialog && (
				<YesNoDialog
					heading="Delete additional name"
					message="Are you sure you want to delete this record?"
					onYes={handleYes}
					onNo={handleNo}
					yesButtonText="Delete"
					noButtonText="Cancel"
					yesButtonIsDelete
				/>
			)}
			{meta.error && (
				<MessageBox warning={!meta.touched ? meta.error : undefined} error={meta.touched ? meta.error : undefined} />
			)}
			{additionalNames?.map((x, i) => {
				const isEditingThisItem = selectedIndex === i && currentAdditionalName === null;
				const isNotEditingAndNotAdding = selectedIndex !== i && currentAdditionalName === null;

				return (
					<div className="arrayItem" key={getObjectValues(x).join()}>
						{isEditingThisItem && (
							<div className="sectionContainer expanded">
								<DataCapturePageSubForm
									values={{ additionalName: x }}
									components={props.props.components}
									validate={validateAdditionalNames(
										props.primaryName,
										additionalNames,
										selectedIndex,
										props.workflowTypeCode
									)}
									onSaveButtonClick={handleSaveButtonClick}
									onCancelButtonClick={resetStatesOfClosingForm}
									saveButtonText="Update"
								/>
							</div>
						)}
						{isNotEditingAndNotAdding && (
							<>
								<legend>
									Name
									{x.fromDate && (
										<>
											<span> </span>
											from <span className="bold">{getLocaleDateString(x.fromDate)}</span>
										</>
									)}
									{x.toDate && (
										<>
											<span> </span>
											to <span className="bold">{getLocaleDateString(x.toDate)}</span>
										</>
									)}
								</legend>
								<div className="sectionContainer">
									<p className="itemDetail">
										{x.firstName} {x.middleNames} {x.lastName}
										{' - '}
										<em>{x.type}</em>
									</p>
									<>
										<DeleteButton onClick={handleDeleteButtonClick(i)} />
										<EditButton onClick={handleEditButtonClick(i)} />
									</>
								</div>
							</>
						)}
					</div>
				);
			})}
			{selectedIndex === null && (
				<div className="arrayItem">
					<legend>Additional Names</legend>
					<div
						className={classNames(
							'sectionContainer addAdditionalName',
							currentAdditionalName !== null ? 'expanded' : null
						)}
					>
						{currentAdditionalName !== null ? (
							<DataCapturePageSubForm
								values={{ additionalName: currentAdditionalName }}
								components={props.props.components}
								validate={validateAdditionalNames(
									props.primaryName,
									additionalNames,
									selectedIndex,
									props.workflowTypeCode
								)}
								onSaveButtonClick={handleSaveButtonClick}
								onCancelButtonClick={resetStatesOfClosingForm}
								saveButtonText="Add"
							/>
						) : (
							<AddAdditionalNamesWrapper>
								<PrimaryButton text="Add Additional Name" onClick={handleAddButtonClick} />
								<MessageInForm>
									Please supply any names you have previously had or are also known by such as Maiden names, Aliases and
									Previous names
								</MessageInForm>
							</AddAdditionalNamesWrapper>
						)}
					</div>
				</div>
			)}
		</>
	);
};

interface Props extends Component {
	primaryName?: PrimaryName;
	setSubmitButtonsDisabled?: (disabled: boolean) => void;
	setBackButtonsDisabled?: (disabled: boolean) => void;
	workflowTypeCode?: string;
	workflowId: string;
}

AdditionalNamesEditor.defaultProps = {
	primaryName: null,
	setSubmitButtonsDisabled: null,
	setBackButtonsDisabled: null,
	workflowTypeCode: null
};

// 845px is the max width specified in kendo
const MessageInForm = styled.p`
	width: 65%;
	display: inline-block;
	margin-left: 25px;

	@media (max-width: 845px) {
		order: 1;
		width: 100%;
		margin-left: 0;
	}
`;

const AddAdditionalNamesWrapper = styled.div`
	display: inline-flex;
	align-items: center;
	@media (max-width: 845px) {
		flex-wrap: wrap;
		> button {
			order: 2;
			display: block;
			margin: 16px 0 0 0 !important; // needed to override kendo style
		}
	}
`;

export default AdditionalNamesEditor;
